import React, { useMemo } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import type { RealtimeParams } from "api/businessDashboard";
import { useRealtimeRevenueQuery } from "queries/Business/Dashboard/useDashboardQueries";

import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";
import { transformSalesDataForRevenueCard } from "constants/businessDashboard";

import { HelpIcon } from "assets/icons/system";
import { ChartIcon } from "assets/icons/businessDashboard";

import { RevenueCard } from "./ui-kit/revenue-card";
import { RealtimeSalesSectionError } from "./sales-section.error";
import { RealtimeSalesSectionSkeleton } from "./sales-section.skeleton";
import { DashboardInnerTabNavigation } from "./inner-tab-navigation";
import { DashboardInnerTabType } from "types/BusinessDashboard";
import { useSelector } from "react-redux";
import { BusinessDashboardHelpHoverCard } from "../ui-kit/help-modal";

dayjs.locale("ko");

const SectionWrapper = styled.div`
  padding: 24px;
  background-color: #f3f7ff;
  position: relative;
`;

const BackColor = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 194px;
  background-color: #0f2859;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  color: #fff;
  position: relative;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.4px;
  font-weight: bold;
  color: #fff;
`;

const HelpButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: pointer;
  background: none;
  border: none;
  transition: color 0.2s;
  &:hover {
    color: #fff;
  }
`;

const SectionTime = styled.p`
  font-size: 12px;
  font-weight: bold;
  line-height: 14px;
  letter-spacing: -0.2px;
  color: #949db2;
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  position: relative;
`;

export const RealtimeSalesSection = () => {
  const today = dayjs().format("YYYY-MM-DD");
  const { searchParams } = useUpdateUrlParams();

  const currentInnerTab = (searchParams.get("innerTab") ??
    "day") as DashboardInnerTabType;

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const apiParams = useMemo(() => {
    const params: RealtimeParams = {
      referenceDate: today,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [today, partnerInfo]);

  const {
    data: revenueData,
    isLoading,
    isError,
    refetch: refetchRevenueData,
  } = useRealtimeRevenueQuery(apiParams);
  const handleRetryRevenueData = () => {
    refetchRevenueData();
  };

  if (isLoading) return <RealtimeSalesSectionSkeleton />;
  if (isError || !revenueData) {
    return <RealtimeSalesSectionError onRetry={handleRetryRevenueData} />;
  }

  const revenueDataToday = transformSalesDataForRevenueCard(
    "오늘",
    dayjs().format("YY.M.D (ddd)"),
    revenueData.today
  );
  const revenueDataThisWeek = transformSalesDataForRevenueCard(
    "이번 주",
    `${dayjs().startOf("week").add(1, "day").format("YY.M.D (ddd)")} ~ ${dayjs().format("YY.M.D (ddd)")}`,
    revenueData.currentWeek
  );
  const revenueDataThisMonth = transformSalesDataForRevenueCard(
    "이번 달",
    `${dayjs().format("YYYY년 M월")}`,
    revenueData.currentMonth
  );

  return (
    <SectionWrapper>
      <BackColor />
      <SectionHeader>
        <ChartIcon />
        <HeaderContent>
          <SectionTitle>실시간 매출</SectionTitle>
          <BusinessDashboardHelpHoverCard type="sales">
            <HelpButton type="button">
              <HelpIcon />
            </HelpButton>
          </BusinessDashboardHelpHoverCard>
          <SectionTime>오늘 {dayjs().format("HH:mm")} 기준</SectionTime>
        </HeaderContent>
      </SectionHeader>
      <CardsGrid>
        <DashboardInnerTabNavigation />
        {currentInnerTab === "day" && (
          <RevenueCard
            type="day"
            data={revenueDataToday}
            bg="#1F6AFF"
            bgGradient="#0F3580"
            categoryColor={revenueDataToday.categoryColor}
            period={revenueDataToday.period}
          />
        )}
        {currentInnerTab === "week" && (
          <RevenueCard
            type="week"
            data={revenueDataThisWeek}
            bg="#133C8F"
            bgGradient="#0A1E48"
            categoryColor={revenueDataThisWeek.categoryColor}
            period={revenueDataThisWeek.period}
          />
        )}
        {currentInnerTab === "month" && (
          <RevenueCard
            type="month"
            data={revenueDataThisMonth}
            bg="#081E48"
            bgGradient="#030F23"
            categoryColor={revenueDataThisMonth.categoryColor}
            period={revenueDataThisMonth.period}
          />
        )}
      </CardsGrid>
    </SectionWrapper>
  );
};
